/**
 * Banner shown when an admin is impersonating a user
 * Displays prominently at the top of the page with an exit button
 */

import { useFetcher } from '@remix-run/react'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from './ui/button'

export type ImpersonationBannerProps = {
	impersonatingUser: {
		username: string
		name: string | null
	}
	adminUser: {
		username: string
		name: string | null
	}
}

export function ImpersonationBanner({
	impersonatingUser,
	adminUser,
}: ImpersonationBannerProps) {
	const fetcher = useFetcher()
	const isExiting = fetcher.state !== 'idle'

	const displayName = impersonatingUser.name || impersonatingUser.username

	return (
		<div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between bg-ember px-4 py-2 text-ink shadow-md">
			<div className="flex items-center gap-2">
				<AlertTriangle className="h-5 w-5" />
				<span className="font-medium">
					Impersonating: <strong>{displayName}</strong>
				</span>
				<span className="text-ink/70">
					(signed in as {adminUser.name || adminUser.username})
				</span>
			</div>
			<fetcher.Form method="post" action="/api/impersonate">
				<input type="hidden" name="intent" value="stop" />
				<Button
					type="submit"
					variant="outline"
					size="sm"
					className="border-ink/30 bg-ember/80 hover:bg-ember/60 text-ink"
					disabled={isExiting}
				>
					<X className="mr-1 h-4 w-4" />
					{isExiting ? 'Exiting...' : 'Exit Impersonation'}
				</Button>
			</fetcher.Form>
		</div>
	)
}
